/**
 * Generates a markdown document for maintenance health analysis.
 *
 * Why this document exists:
 * - Provides a shareable, exportable summary of repository health
 * - Can be saved/printed for compliance or team discussions
 * - Contains actionable details covering all 6 maintenance pillars
 */

import type {
  DriftReport,
  MaintenanceScoreResult,
  PillarScore,
  DependencyPillarDetails,
  GeneratedDocument,
} from '../../types/analysis.types';

import {
  formatPillarName,
  formatWeight,
  generatePillarSection,
  getPillarGrade,
  gradeDescriptions,
} from './pillarSections';

interface DocumentContext {
  repositoryName?: string;
  primaryLanguage?: string;
}

/**
 * Generate maintenance health document from MaintenanceScoreResult.
 * Falls back to drift-only report if maintenanceScore not available.
 */
export function generateDriftDocument(
  drift: DriftReport,
  maintenanceScore?: MaintenanceScoreResult | null,
  context?: DocumentContext,
): GeneratedDocument {
  // Use full maintenance score if available, otherwise fall back to drift-only
  const repoName = context?.repositoryName || drift.repositoryName || 'Unknown';
  const language = context?.primaryLanguage || drift.primaryLanguage || 'Unknown';
  const analysisDate =
    maintenanceScore?.collectedAt || drift.analysisDate || new Date().toISOString();

  // If we have full maintenance score, use it
  if (maintenanceScore && maintenanceScore.pillarScores?.length > 0) {
    const { overallScore, grade, pillarScores, hardCapsApplied } = maintenanceScore;

    // Sort pillars by weight descending for consistent display
    const sortedPillars = [...pillarScores].sort((a, b) => b.weight - a.weight);

    const pillarSummary = sortedPillars
      .map(
        (p) =>
          `| ${formatPillarName(p.name)} | ${p.score} | ${getPillarGrade(p.score)} | ${formatWeight(p.weight)} |`,
      )
      .join('\n');

    const pillarDetails = sortedPillars
      .map((p) => generatePillarSection(p, drift))
      .join('\n---\n\n');

    const hardCapsSection =
      hardCapsApplied && hardCapsApplied.length > 0
        ? `\n> ⚠️ **Hard Caps Applied:** ${hardCapsApplied.join(', ')}\n`
        : '';

    const content = `# SDLC Report

**Repository:** ${repoName}
**Language:** ${language}
**Analysis Date:** ${analysisDate}

---

## Overall Score

| Metric | Value |
|--------|-------|
| **Maintenance Score** | **${overallScore}/100** |
| **Grade** | **${grade}** - ${gradeDescriptions[grade] || 'Assessment pending'} |
${hardCapsSection}

---

## 6-Pillar Assessment

| Pillar | Score | Grade | Weight |
|--------|-------|-------|--------|
${pillarSummary}

---

## Pillar Details

${pillarDetails}

---

## Recommended Actions

${generateRecommendations(sortedPillars, drift)}

---

*Generated by Autar - [autar.dev](https://autar.dev)*
`;

    return {
      fileName: 'maintenance-health-report.md',
      title: 'SDLC Report',
      content,
    };
  }

  // Fallback: drift-only report (legacy behavior)
  return generateDriftOnlyDocument(drift, repoName, language, analysisDate);
}

function generateRecommendations(pillars: PillarScore[], drift?: DriftReport): string {
  const recommendations: string[] = [];
  const weakPillars = pillars.filter((p) => p.score < 50).sort((a, b) => a.score - b.score);

  for (const pillar of weakPillars.slice(0, 3)) {
    switch (pillar.name) {
      case 'activity':
        recommendations.push(
          '**Increase Activity:** Encourage more frequent commits and contributor engagement.',
        );
        break;
      case 'cicd':
        recommendations.push('**Improve CI/CD:** Set up automated builds, tests, and deployments.');
        break;
      case 'issues_prs':
        recommendations.push(
          '**Manage Issues/PRs:** Create issue/PR templates and improve response times.',
        );
        break;
      case 'dependency': {
        const depDetails = pillar.details as DependencyPillarDetails;
        if (!depDetails.hasDependabot && !depDetails.hasRenovate) {
          recommendations.push(
            '**Enable Dependency Automation:** Set up Dependabot or Renovate for automated updates.',
          );
        } else {
          recommendations.push('**Update Dependencies:** Review and update outdated packages.');
        }
        break;
      }
      case 'release':
        recommendations.push(
          '**Establish Release Process:** Create regular releases with semantic versioning.',
        );
        break;
      case 'metadata':
        recommendations.push(
          '**Complete Documentation:** Add missing README, LICENSE, and contributing guidelines.',
        );
        break;
    }
  }

  if (drift?.security?.riskLevel === 'high' || drift?.security?.riskLevel === 'critical') {
    recommendations.unshift(
      '**🚨 Security Priority:** Run security scans - significant vulnerability exposure detected.',
    );
  }

  return recommendations.length > 0
    ? recommendations.map((r, i) => `${i + 1}. ${r}`).join('\n')
    : '✅ Repository is in good health. Continue current maintenance practices.';
}

function generateDriftOnlyDocument(
  drift: DriftReport,
  repoName: string,
  language: string,
  analysisDate: string,
): GeneratedDocument {
  const score = drift.score ?? 0;
  const grade = drift.grade || 'D';
  const totalDeps = drift.metrics?.totalDependencies ?? 0;
  const outdatedDeps = drift.metrics?.outdatedDependencies ?? 0;
  const driftRatio = drift.metrics?.driftRatio ?? 0;
  const oldestYears = drift.metrics?.oldestPinnedYears ?? 0;
  const riskLevel = drift.security?.riskLevel || 'low';
  const scanTools = drift.security?.scanTools || ['Snyk', 'Trivy', 'OWASP Dependency-Check'];

  const content = `# SDLC Report

**Repository:** ${repoName}
**Language:** ${language}
**Analysis Date:** ${analysisDate}

---

## Dependency Health Summary

| Metric | Value |
|--------|-------|
| **Dependency Score** | ${score}/100 (Grade ${grade}) |
| **Total Dependencies** | ${totalDeps} |
| **Packages Needing Updates** | ${outdatedDeps} (${Math.round(driftRatio * 100)}%) |
| **Oldest Package** | ${(oldestYears ?? 0).toFixed(1)} years behind |
| **Security Risk** | ${riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1)} |

${drift.interpretation?.summary || 'Dependency analysis complete.'}

---

## Top Packages to Update

| Package | Current | Latest | Age |
|---------|---------|--------|-----|
${
  (drift.staleDependencies || [])
    .slice(0, 10)
    .map(
      (dep) =>
        `| ${dep.name ?? 'unknown'} | ${dep.pinned ?? '-'} | ${dep.latest ?? '-'} | ${(dep.ageYears ?? 0).toFixed(1)}yr |`,
    )
    .join('\n') || '| No outdated packages found | - | - | - |'
}

---

## Recommended Security Scanners

${scanTools.map((tool) => `- ${tool}`).join('\n')}

---

*Generated by Autar - [autar.dev](https://autar.dev)*
`;

  return {
    fileName: 'maintenance-health-report.md',
    title: 'SDLC Report',
    content,
  };
}

export default generateDriftDocument;
